/**
 * Error envelope handling (W-X-03).
 *
 * Mirrors the backend envelope (api-contract.md §1.3). Clients branch on `code`
 * (the contract) and display `message` (the localised string).
 */

export interface ApiErrorDetail {
  code: string;
  message: string;
  details: Record<string, unknown>;
  request_id: string | null;
}

export interface ApiErrorEnvelope {
  error: ApiErrorDetail;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(status: number, detail: ApiErrorDetail) {
    super(detail.message);
    this.name = "ApiError";
    this.status = status;
    this.code = detail.code;
    this.details = detail.details ?? {};
    this.requestId = detail.request_id ?? null;
  }
}

export function isApiErrorEnvelope(body: unknown): body is ApiErrorEnvelope {
  return (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof (body as ApiErrorEnvelope).error?.code === "string"
  );
}

/**
 * The user-facing string for any thrown error. For an ApiError we show the
 * backend-supplied `message` (already localised per api-contract.md §1.3);
 * anything else falls back to a generic line. Use this for toast notifications
 * on failed queries/mutations.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

interface FieldError {
  loc?: (string | number)[];
  msg?: string;
  /** Human label the API adds, e.g. "Quantity (item 1)". */
  field?: string;
}

// What the API sent for every 422 before it started naming the field in `message`.
const GENERIC_VALIDATION_MESSAGE = "Request validation failed.";

/**
 * A detailed, user-facing message for form submissions. Unlike `errorMessage`, this:
 *  - shows a 422 VALIDATION_ERROR's message, which names the field ("Phone is required.",
 *    "Quantity (item 1): Input should be greater than 0"); against an older API that only sent
 *    the generic line, it builds "field: reason" from the first field error instead;
 *  - distinguishes a transport/CORS failure (fetch rejects with a TypeError — NOT an ApiError)
 *    from a real server error, so the user isn't shown a misleading generic line.
 */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const fieldErrors = err.details?.field_errors as FieldError[] | undefined;
    if (
      err.code === "VALIDATION_ERROR" &&
      err.message === GENERIC_VALIDATION_MESSAGE &&
      fieldErrors?.length
    ) {
      const fe = fieldErrors[0];
      // loc is like ["body", "unit"]; drop the leading "body" for a clean field name.
      const field = fe.field || (fe.loc ?? []).filter((p) => p !== "body").join(".");
      const reason = (fe.msg ?? err.message).replace(/^Value error, /, "");
      return field ? `${field}: ${reason}` : reason;
    }
    return err.message;
  }
  // Not an ApiError → the request never reached/returned from the server (network down, CORS,
  // server not running). fetch throws a TypeError here, which is what lands in this branch.
  if (err instanceof TypeError) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
}
